import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import gridService from '../services/gridService';

const useGridStore = create(
  devtools((set, get) => ({
    // State
    currentGrid: null,
    grids: [],
    isLoading: false,
    error: null,

    // Actions
    // Fetch grid for specific month and platform
    fetchGrid: async (projectId, month, platform = 'instagram') => {
      set({ isLoading: true, error: null });
      try {
        const response = await gridService.getGrid(projectId, month, platform);
        
        set({ 
          currentGrid: response.data,
          isLoading: false 
        });
        
        return response.data;
      } catch (error) {
        set({ 
          error: error.message || 'Failed to fetch grid',
          isLoading: false 
        });
        throw error;
      }
    },

    // Update grid configuration
    updateGrid: async (gridId, updates) => {
      set({ isLoading: true, error: null });
      try {
        const response = await gridService.updateGrid(gridId, updates);
        
        set({ 
          currentGrid: response.data,
          isLoading: false 
        });
        
        return response.data;
      } catch (error) {
        set({ 
          error: error.message || 'Failed to update grid',
          isLoading: false 
        });
        throw error;
      }
    },

    // Copy grid from another month
    copyGrid: async (projectId, targetMonth, sourceMonth, platform = 'instagram') => {
      set({ isLoading: true, error: null });
      try {
        const response = await gridService.copyGrid(projectId, targetMonth, sourceMonth, platform);
        
        set({ 
          currentGrid: response.data,
          isLoading: false 
        });
        
        return response.data;
      } catch (error) {
        set({ 
          error: error.message || 'Failed to copy grid',
          isLoading: false 
        });
        throw error;
      }
    },

    // Assign task to cell
    assignTaskToCell: async (gridId, taskId, row, column) => {
      set({ isLoading: true, error: null });
      try {
        const response = await gridService.assignTask(gridId, taskId, row, column);

        set({
          currentGrid: response.data,
          isLoading: false
        });

        return response.data;
      } catch (error) {
        set({
          error: error.message || 'Failed to assign task',
          isLoading: false
        });
        throw error;
      }
    },

    // Move task between cells
    moveTaskBetweenCells: async (gridId, taskId, fromRow, fromColumn, toRow, toColumn) => {
      set({ isLoading: true, error: null });
      try {
        const response = await gridService.moveTask(gridId, taskId, fromRow, fromColumn, toRow, toColumn);

        set({
          currentGrid: response.data,
          isLoading: false
        });

        return response.data;
      } catch (error) {
        set({
          error: error.message || 'Failed to move task',
          isLoading: false
        });
        throw error;
      }
    },

    // Remove task from cell
    removeTaskFromCell: async (gridId, taskId, row, column) => {
      set({ isLoading: true, error: null });
      try {
        const response = await gridService.removeTask(gridId, taskId, row, column);

        set({
          currentGrid: response.data,
          isLoading: false
        });

        return response.data;
      } catch (error) {
        set({
          error: error.message || 'Failed to remove task',
          isLoading: false
        });
        throw error;
      }
    },

    // Fetch grids by date range
    fetchGridsByRange: async (projectId, startDate, endDate) => {
      set({ isLoading: true, error: null });
      try {
        console.log('fetchGridsByRange called with:', { projectId, startDate, endDate });
        
        // Validate dates
        if (!startDate || !endDate || startDate === 'Invalid Date' || endDate === 'Invalid Date') {
          throw new Error('Invalid date range provided');
        }
        
        const response = await gridService.getGridsByRange(projectId, startDate, endDate);
        
        set({ 
          grids: response.data,
          isLoading: false 
        });
        
        return response.data;
      } catch (error) {
        set({ 
          error: error.message || 'Failed to fetch grids',
          isLoading: false 
        });
        throw error;
      }
    },

    // Update cell tag assignment
    updateCellTag: async (gridId, row, column, tagId) => {
      const { currentGrid } = get();
      if (!currentGrid) return;

      // Find or create cell
      let cells = [...currentGrid.cells];
      let cellIndex = cells.findIndex(c => c.row === row && c.column === column);
      
      if (cellIndex >= 0) {
        cells[cellIndex].tagId = tagId;
      } else {
        cells.push({ row, column, tagId, taskIds: [] });
      }

      return get().updateGrid(gridId, { cells });
    },

    // Clear store
    clearGridStore: () => {
      set({
        currentGrid: null,
        grids: [],
        isLoading: false,
        error: null
      });
    }
  }))
);

export default useGridStore;